import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";

import { useCanonicalUrl } from "shared/hooks";

import { PageSeo } from "scmp-app/components/meta";
import type { videoSeoVideo$key } from "scmp-app/queries/__generated__/videoSeoVideo.graphql";

type Props = {
  reference: videoSeoVideo$key;
};

export const VideoSeo: FunctionComponent<Props> = ({ reference }) => {
  const video = useFragment(
    graphql`
      fragment videoSeoVideo on Video {
        headline
        urlAlias
        summary {
          text
        }
        subHeadline {
          text
        }
        image(filter: { type: MAIN_IMAGE }) {
          style(filter: { style: "wide_landscape" }) {
            url
          }
        }
      }
    `,
    reference,
  );

  const { canonicalUrl } = useCanonicalUrl(video.urlAlias);

  // Fall back to the site level description only when the video carries no description at all
  const description =
    findFirstNonEmptyString(
      first(video.summary?.text?.split("\n")),
      first(video.subHeadline?.text?.split("\n")),
    ) || undefined;

  const imageUrl = video.image?.style?.url;

  return (
    <PageSeo
      canonical={canonicalUrl}
      description={description}
      openGraph={{
        images: imageUrl ? [{ url: imageUrl }] : [],
        type: "article",
        url: canonicalUrl,
      }}
      title={video.headline ?? undefined}
    />
  );
};

VideoSeo.displayName = "VideoSeo";
